import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/packages');
        const data = await response.json();
        
        // Sorter pakkerne så Golfpakke A (f.eks. Golfpakke 1 A) altid står først
        const sortedData = [...data].sort((a, b) => {
          const titleA = (a.title?.da || '').toLowerCase();
          const titleB = (b.title?.da || '').toLowerCase();
          const isA = titleA.includes('1 a') || titleA.includes('1a') || titleA.endsWith(' a');
          const isB = titleB.includes('1 a') || titleB.includes('1a') || titleB.endsWith(' a');
          if (isA && !isB) return -1;
          if (!isA && isB) return 1;
          return titleA.localeCompare(titleB);
        });
        
        setPackages(sortedData);
      } catch (error) {
        console.error('Error fetching packages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);

  if (loading) return null;

  const visiblePackages = showAll ? packages : packages.slice(0, 2);

  return (
    <section id="packages" className="pt-32 pb-24 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Text Content */}
          <div className="lg:col-span-5">
            <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-8 uppercase leading-tight">
              Golf i det autentiske Spanien
            </h2>
            <div className="space-y-6 text-slate-600 text-lg leading-relaxed mb-10">
              <p>
                Spil golf nær Europas ældste by – Cádiz. Oplev stemningen og kulturen på tætteste hold i den lille kystby La Barrosa – de spanske dansende heste, sherry fra Jerez og friskfanget skaldyr og fisk hver dag.
              </p>
              <p>
                Golf i Andalusien er en autentisk oplevelse både på og udenfor banen. Spørg efter Green Fee aftaler – vi har rigtig gode aftaler med de fleste baner i området, for eksempel Montenmedio, Sherry golf, Argos garden og Costa Bellena.
              </p>
            </div>
            
            {packages.length > 2 && (
              <button 
                onClick={() => setShowAll(!showAll)}
                className="inline-flex items-center gap-2 text-slate-900 font-semibold uppercase tracking-wider hover:gap-3 transition-all group"
              >
                {showAll ? 'Vis færre pakker' : 'Se alle pakker'}
                <ArrowRight className={`w-5 h-5 transition-transform ${showAll ? '-rotate-90' : 'group-hover:translate-x-1'}`} />
              </button>
            )}
          </div>

          {/* Right Column: Packages Grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {visiblePackages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex flex-col bg-white rounded-2xl overflow-hidden border border-slate-200/50 shadow-sm hover:shadow-xl transition-all duration-300 group h-full"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={pkg.image[0]} 
                      alt={pkg.title.da} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-slate-900 mb-1 uppercase leading-tight">{pkg.title.da}</h3>
                      <p className="text-slate-900 font-semibold">{pkg.price_text.da}</p>
                    </div>
                    
                    <div className="text-slate-600 text-xs leading-relaxed mb-6 flex-grow prose prose-slate prose-xs max-w-none">
                      <div dangerouslySetInnerHTML={{ __html: pkg.details.da }} className="[&>ul]:list-disc [&>ul]:pl-4 [&>li]:mb-1" />
                    </div>

                    <Link 
                      to={`/kontakt`} 
                      className="inline-flex items-center justify-center w-full py-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold hover:-translate-y-1 transition-all uppercase tracking-wider"
                    >
                      Book dit ophold
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Packages;
